import React, {useState, useCallback} from 'react';
import {connect} from 'react-redux';
import {useAsyncEffect} from 'use-async-effect';
import flatten from 'lodash/flatten';
import moment from 'moment';
import ReactModal from 'react-modal';
import {toast} from 'react-toastify';

import Timeline from './Roadmap/Timeline';
import {AppState} from './Store';
import {
  RoadmapService,
  Roadmap as RoadmapModel,
  RoadmapGroup as RoadmapGroupModel,
  RoadmapItem as RoadmapItemModel,
} from './Services/RoadmapService';

import NewItem, {NewRoadmapItem} from './Roadmap/NewItem';
import EditItem from './Roadmap/EditItem';

interface RoadmapProps {
  roadmapService: RoadmapService;
  authorised: boolean;
  admin: boolean;
}

export type RoadmapGroup = Omit<RoadmapGroupModel, "items">;

type RoadmapItem = RoadmapItemModel & {
  canMove: boolean;
  canResize: true | false | 'left' | 'right' | 'both';
  canChangeGroup: boolean;
  itemProps?: any;
};

type ItemProps = Pick<RoadmapItem, "canMove" | "canResize" | "canChangeGroup" | "itemProps" | "group">;

type DialogType = undefined | 'newItem' | 'editItem';

function Roadmap({
  roadmapService,
  authorised,
  admin,
}: RoadmapProps) {
  const [roadmap, setRoadmap] = useState<RoadmapModel | undefined>();
  const [items, setItems] = useState<RoadmapItem[]>([]);
  const [groups, setGroups] = useState<RoadmapGroup[]>([]);

  const [dialog, setDialog] = useState<DialogType>();
  const [clickTime, setClickTime] = useState(0);
  const [clickId, setClickId] = useState(0);

  const loadRoadmap = useCallback((newRoadmap: RoadmapModel) => {
    setRoadmap(newRoadmap);
    setGroups(mapGroups(newRoadmap));
    setItems(mapItems(newRoadmap));
  }, [admin]);

  useAsyncEffect(async isMounted => {
    try {
      if (!authorised) {
        return;
      }

      const result = await roadmapService.byId(1);
      if (result.parsedBody !== undefined && isMounted) {
        loadRoadmap(result.parsedBody);
      }
    } catch (e) {
      console.log("whoops", e);
    }
  }, [authorised, loadRoadmap]);

  const mapGroups = (roadmap?: RoadmapModel): RoadmapGroup[] => {
    if (roadmap === undefined) {
      return [];
    }

    return roadmap.groups.map(g => {
      const {items, ...rest} = g;

      return rest;
    });
  };

  const mapItems = (roadmap?: RoadmapModel): RoadmapItem[] => {
    if (roadmap === undefined) {
      return [];
    }

    return flatten(roadmap.groups.map(g => {
      return g.items.map(i => {
        return {
          ...i,
          ...getItemProps(g),
        };
      });
    }));
  };

  const getItemProps = (group: RoadmapGroup): ItemProps => ({
    group: group.id,
    canMove: admin,
    canResize: admin ? 'both' : false,
    canChangeGroup: admin,
    itemProps: {
      style: {
        background: group.colour,
        color: '#222',
      },
    },
  });

  const set = <T extends unknown>(array: T[], index: number, val: T) => {
    const ret = array.slice(0);
    ret[index] = val;
    return ret;
  };

  const onItemMove = (itemId: number, time: number, newGroupOrder: number) => {
    const itemIndex = items.findIndex(i => i.id === itemId);
    const item = items[itemIndex];

    const newStart = moment(time);
    const diff = moment(item.endDate).diff(item.startDate);
    const newEnd = newStart.clone().add(diff, 'ms');
    const newGroup = groups[newGroupOrder];

    const newItem = {
      ...item,
      ...getItemProps(newGroup),
      startDate: newStart.toISOString(),
      endDate: newEnd.toISOString(),
    };

    const newItems = set(items, itemIndex, newItem);
    setItems(newItems);
  };

  const onItemResize = (itemId: number, time: number, edge: 'left' | 'right') => {
    const itemIndex = items.findIndex(i => i.id === itemId);
    const item = items[itemIndex];
    const newStart = edge === 'left'
      ? moment(time).toISOString()
      : item.startDate;
    const newEnd = edge === 'right'
      ? moment(time).toISOString()
      : item.endDate;
    const newItem = {
      ...item,
      startDate: newStart,
      endDate: newEnd,
    };

    const newItems = set(items, itemIndex, newItem);
    setItems(newItems);
  };

  const onItemDoubleClick = (itemId: number, e: React.MouseEvent, time: number) => {
    setClickTime(time);
    setClickId(itemId);
    setDialog('editItem');
  };

  const onCanvasClick = (groupId: number, time: number) => {
    setClickTime(time);
    setClickId(groupId);
    setDialog('newItem');
  };

  const createItem = (newItem: NewRoadmapItem) => {
    const newId = Math.min(0, Math.min.apply(null, items.map(i => i.id))) - 1;
    const group = groups.find(g => g.id === newItem.group);
    if (!group) {
      toast.error("That group doesn't exist");
      return;
    }

    const newItems = [...items, {
      ...newItem,
      ...getItemProps(group),
      id: newId,
    }];

    setItems(newItems);
    setDialog(undefined);
  };

  const updateItem = (item: RoadmapItemModel) => {
    const index = items.findIndex(i => i.id === item.id);
    const oldItem = items[index];

    if (!oldItem) {
      toast.error("Can't edit that which does not exist");
      return;
    }

    const group = groups.find(g => g.id === item.group);
    if (!group) {
      toast.error("That group doesn't exist");
      return;
    }

    const newItem = {
      ...item,
      ...getItemProps(group),
    };

    const newItems = set(items, index, newItem);
    setItems(newItems);

    setDialog(undefined);
  };

  const deleteItem = (id: number) => {
    const index = items.findIndex(i => i.id === id);
    if (index === -1) {
      toast.error("Can't delete that which does not exist");
      return;
    }

    const newItems = [
      ...items.slice(0, index),
      ...items.slice(index + 1),
    ];
    setItems(newItems);

    setDialog(undefined);
  };

  const saveRoadmap = async () => {
    if (!roadmap) {
      return;
    }

    const newGroups: RoadmapGroupModel[] = groups.map(g => ({
      ...g,
      items: items.filter(i => i.group === g.id),
    }));

    const newRoadmap = {
      ...roadmap,
      groups: newGroups,
    };

    try {
      const response = await roadmapService.update(newRoadmap);
      if (response.parsedBody) {
        loadRoadmap(response.parsedBody);
      }

      toast.success("Changes saved");
    } catch (e) {
      toast.error("Failed to save changes");
      console.error(e);
    }
  };

  const modalContent = () => {
    const item = items.find(i => i.id === clickId);
    switch (dialog) {
    case 'newItem':
      return (
        <NewItem
          time={clickTime}
          groupId={clickId}
          groups={groups}
          create={createItem}
          close={() => setDialog(undefined)}
        />
      );
    case 'editItem':
      if (!item) {
        return undefined;
      }

      return (
        <EditItem
          item={item}
          groups={groups}
          update={updateItem}
          remove={deleteItem}
          close={() => setDialog(undefined)}
        />
      );
    default:
      return undefined;
    }
  };

  return (
    <>
      <div className="d-flex flex-column vh-100">
        <div className="flex-grow-1">
          <Timeline
            groups={groups}
            items={items}
            onItemMove={onItemMove}
            onItemResize={onItemResize}
            onItemDoubleClick={onItemDoubleClick}
            onCanvasDoubleClick={onCanvasClick}
          />
        </div>
        {admin && (
          <div className="align-self-end m-2">
            <button className="btn btn-primary" onClick={saveRoadmap}>Save</button>
          </div>
        )}
      </div>
      <ReactModal
        isOpen={dialog !== undefined}
        onRequestClose={() => setDialog(undefined)}
        ariaHideApp={false}
      >
        {modalContent()}
      </ReactModal>
    </>
  );
}

const mapStateToProps = (state: AppState) => {
  return {
    roadmapService: state.auth.roadmapService,
    authorised: state.auth.authorised,
    admin: state.auth.admin,
  };
};

export default connect(mapStateToProps)(Roadmap);
